import React, {useEffect, useMemo, useState} from 'react'
import './CreationPage.css'
import {BACKEND_URL} from '../../utils/loadEnv'
import {useNavigate, useParams} from "react-router-dom";

type Issuer = 'verlag' | 'gmbh'
type CountryCode = 'DE' | 'AT' | ''

type RecipientAddress = {
    line1: string
    line2: string
    street: string
    zip: string
    city: string
    country: CountryCode
    contactEmail: string
}

type DeliveryAddress = {
    line1: string
    line2: string
    street: string
    zip: string
    city: string
    country: CountryCode
}

type FormState = {
    invoiceSequence: number
    recipient: RecipientAddress
    delivery: DeliveryAddress
    orderNumber: string
    orderDate: string
    invoiceDate: string
    weightKg: string
    dhlTrackingNumber: string
    sendEmail: string
    sendSubject: string
    sendText: string
}

type SaveState = 'unsaved' | 'saved' | 'error'

type BackendProduct = {
    id: number
    name: string
    information: string | null
    price: number
    list_for_publisher: boolean
    isbn_number: string | null
}

type ProductsLoadState = 'idle' | 'loading' | 'loaded' | 'error'

type VerlagLine = {
    productId: number
    quantity: string
    discountPercent: string
    discountTouched: boolean
}

type GmbhLine = {
    id: string
    name: string
    information: string
    price: string
}

const PUBLISHER_VAT = 0.07
const GMBH_VAT = 0.19

const VERLAG_DEFAULTS: Pick<FormState, 'recipient' | 'delivery' | 'sendEmail' | 'sendSubject' | 'sendText'> = {
    recipient: {
        line1: 'Kolibri 360 GmbH',
        line2: 'Rechnungsprüfung',
        street: 'Industriestraße 23',
        zip: '70565',
        city: 'Stuttgart',
        country: 'DE',
        contactEmail: 'invoice@kolibri360.de',
    },
    delivery: {
        line1: 'Kolibri 360 GmbH',
        line2: 'Kolibri360 Barsortiment 20008',
        street: 'Ferdinand-Jühlke-Strasse 7',
        zip: '99095',
        city: 'Erfurt',
        country: 'DE',
    },
    sendEmail: 'invoice@kolibri360.de',
    sendSubject: 'Ihre Rechnung zur Bestellung [Bestellungsnummer] vom [Bestellungsdatum]',
    sendText: `Sehr geehrte Damen und Herren,

vielen Dank für Ihre Bestellung [Bestellungsnummer] vom [Bestellungsdatum].

Anbei übersenden wir Ihnen die Rechnung als PDF-Dokument mit integrierter E-Rechnung.

Für Rückfragen stehen wir jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen
Terence Böhme
Verlagsinhaber`
}

const EMPTY_RECIPIENT: RecipientAddress = {
    line1: '',
    line2: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
    contactEmail: '',
}

const EMPTY_DELIVERY: DeliveryAddress = {
    line1: '',
    line2: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
}

const getYearShort = () => {
    const year = new Date().getFullYear() % 100
    return String(year).padStart(2, '0')
}

const formatInvoiceNumber = (issuer: Issuer, sequence: number) => {
    const prefix = issuer === 'verlag' ? 'V' : 'G'
    const yearShort = getYearShort()
    const seq = String(sequence).padStart(4, '0')
    return `${prefix}-${yearShort}-${seq}`
}

const buildInitialState = (issuer: Issuer): { form: FormState; defaultsActive: boolean } => {
    const today = new Date().toLocaleDateString('de-DE', {
        day: 'numeric',
        month: '2-digit',
        year: 'numeric',
    })

    if (issuer === 'verlag') {
        return {
            defaultsActive: true,
            form: {
                invoiceSequence: -1,
                recipient: {...VERLAG_DEFAULTS.recipient},
                delivery: {...VERLAG_DEFAULTS.delivery},
                orderNumber: '',
                orderDate: today,
                invoiceDate: '',
                weightKg: '',
                dhlTrackingNumber: '',
                sendEmail: VERLAG_DEFAULTS.sendEmail,
                sendSubject: VERLAG_DEFAULTS.sendSubject,
                sendText: VERLAG_DEFAULTS.sendText,
            }
        }
    }

    return {
        defaultsActive: false,
        form: {
            invoiceSequence: -1,
            recipient: {...EMPTY_RECIPIENT},
            delivery: {...EMPTY_DELIVERY},
            orderNumber: '',
            orderDate: today,
            invoiceDate: '',
            weightKg: '',
            dhlTrackingNumber: '',
            sendEmail: '',
            sendSubject: '',
            sendText: `Sehr geehrte Damen und Herren,

vielen Dank für Ihre Bestellung [Bestellungsnummer] vom [Bestellungsdatum].

Anbei übersenden wir Ihnen die Rechnung als PDF-Dokument mit integrierter E-Rechnung.

Für Rückfragen stehen wir jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen
Terence Böhme
Geschäftsführer`
        }
    }
}

const isBlank = (v: string) => v.trim().length === 0

const validateForm = (state: FormState, currentIssuer: Issuer) => {
    const invalid = new Set<string>()

    if (isBlank(state.recipient.line1)) invalid.add('recipient.line1')
    if (isBlank(state.recipient.street)) invalid.add('recipient.street')
    if (isBlank(state.recipient.zip)) invalid.add('recipient.zip')
    if (isBlank(state.recipient.city)) invalid.add('recipient.city')
    if (isBlank(state.recipient.country)) invalid.add('recipient.country')
    if (isBlank(state.sendEmail)) invalid.add('sendEmail')
    if (isBlank(state.sendSubject)) invalid.add('sendSubject')
    if (isBlank(state.sendText)) invalid.add('sendText')

    if (currentIssuer === 'verlag') {
        if (isBlank(state.delivery.line1)) invalid.add('delivery.line1')
        if (isBlank(state.delivery.street)) invalid.add('delivery.street')
        if (isBlank(state.delivery.zip)) invalid.add('delivery.zip')
        if (isBlank(state.delivery.city)) invalid.add('delivery.city')
        if (isBlank(state.delivery.country)) invalid.add('delivery.country')

        const isKolibri360 = state.recipient.line1.trim() === 'Kolibri 360 GmbH'
        if (isKolibri360) {
            if (isBlank(state.dhlTrackingNumber)) invalid.add('dhlTrackingNumber')
        }
    }

    return invalid
}

const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

const parseNumberFromInput = (v: string) => {
    const normalized = v.replace(',', '.').trim()
    if (normalized.length === 0) return 0
    const n = Number(normalized)
    return Number.isFinite(n) ? n : 0
}

const formatEuro = (v: number) => {
    const safe = Number.isFinite(v) ? v : 0
    return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(safe)
}

const defaultPublisherDiscount = (unitNet: number) => {
    const grossUnit = unitNet * (1 + PUBLISHER_VAT)
    return grossUnit > 10 ? '50' : '55'
}

const buildInitialVerlagLines = (products: BackendProduct[]): VerlagLine[] => {
    return products
        .filter((p) => Boolean(p.list_for_publisher))
        .map((p) => ({
            productId: p.id,
            quantity: '0',
            discountPercent: defaultPublisherDiscount(Math.max(0, p.price)),
            discountTouched: false,
        }))
}

const newGmbhLine = (): GmbhLine => ({
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    name: '',
    information: '',
    price: '',
})

const buildInitialGmbhLines = (): GmbhLine[] => [newGmbhLine()]

type NextInvoiceIdResponse = {
    success: boolean
    message: string
    next_invoice_id: number | null
}

const extractProductsFromResponse = (raw: any): { success: boolean; message: string; data: any[] } => {
    const body =
        raw && typeof raw === 'object' && raw.body && typeof raw.body === 'object' ? raw.body : raw

    return {
        success: Boolean(body?.success),
        message: typeof body?.message === 'string' ? body.message : '',
        data: Array.isArray(body?.data) ? body.data : [],
    }
}

const extractInvoiceIdFromResponse = (raw: unknown): NextInvoiceIdResponse => {
    const body =
        raw && typeof raw === 'object' && 'body' in raw && (raw as any).body && typeof (raw as any).body === 'object'
            ? (raw as any).body
            : raw

    const b = body as any

    return {
        success: Boolean(b?.success),
        message: typeof b?.message === 'string' ? b.message : '',
        next_invoice_id: typeof b?.next_invoice_id === 'number' ? b.next_invoice_id : null,
    }
}

type GetInvoiceResponse = {
    success: boolean
    message: string
    data: any | null
}

const extractGetInvoiceFromResponse = (raw: unknown): GetInvoiceResponse => {
    const body =
        raw && typeof raw === 'object' && 'body' in raw && (raw as any).body && typeof (raw as any).body === 'object'
            ? (raw as any).body
            : raw

    const b = body as any

    return {
        success: Boolean(b?.success),
        message: typeof b?.message === 'string' ? b.message : '',
        data: b?.data ?? null
    }
}

const normalizeProducts = (items: any[]): BackendProduct[] => {
    return items
        .filter((p) => p && typeof p === 'object')
        .map((p) => ({
            id: Number(p.id),
            name: String(p.name ?? ''),
            information: p.information == null ? null : String(p.information),
            price: Number(p.price ?? 0),
            list_for_publisher: Boolean(p.list_for_publisher),
            isbn_number: p.isbn_number == null ? null : String(p.isbn_number),
        }))
        .filter((p) => Number.isFinite(p.id) && p.id > 0 && p.name.trim().length > 0)
}

const CreationPage: React.FC<{ isCreation: boolean }> = ({isCreation}) => {
    const {invoiceId: editingId} = useParams()

    const [{form: initialForm, defaultsActive: initialDefaultsActive}] = useState(() =>
        buildInitialState('verlag')
    )

    const [issuer, setIssuer] = useState<Issuer>('verlag')
    const [zeitfrachtDefaultsActive, setZeitfrachtDefaultsActive] = useState<boolean>(
        initialDefaultsActive
    )
    const [form, setForm] = useState<FormState>(initialForm)

    const [saveState, setSaveState] = useState<SaveState>(isCreation ? 'unsaved' : 'saved')
    const [saveMessage, setSaveMessage] = useState(isCreation ? 'Noch nicht gespeichert' : 'Gespeichert')
    const [invalidPaths, setInvalidPaths] = useState<Set<string>>(new Set())

    const [isSendModalOpen, setIsSendModalOpen] = useState(false)

    const [productsLoadState, setProductsLoadState] = useState<ProductsLoadState>('idle')
    const [productsError, setProductsError] = useState('')
    const [products, setProducts] = useState<BackendProduct[]>([])

    const [invoiceIdLoadState, setInvoiceIdLoadState] = useState<ProductsLoadState>('idle')
    const [invoiceIdError, setInvoiceIdError] = useState('')
    const [invoiceId, setInvoiceId] = useState<number | null>(null)

    const [verlagLines, setVerlagLines] = useState<VerlagLine[]>([])
    const [verlagOnlySelected, setVerlagOnlySelected] = useState(false)

    const [gmbhLines, setGmbhLines] = useState<GmbhLine[]>(() => buildInitialGmbhLines())

    const [sendConfirmOpen, setSendConfirmOpen] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [sendError, setSendError] = useState<string | null>(null)

    const hasAnyVerlagProduct = () => {
        return verlagLines.some(
            (line) => Math.floor(parseNumberFromInput(line.quantity)) > 0
        )
    }

    const hasAnyValidGmbhProduct = () => gmbhLines.some(isValidGmbhLine)

    useEffect(() => {
        const controller = new AbortController()

        const loadProducts = async (): Promise<BackendProduct[] | null> => {
            if (!BACKEND_URL || BACKEND_URL.trim().length === 0) {
                setProductsLoadState('error')
                setProductsError('BACKEND_URL fehlt (REACT_APP_BACKEND_URL).')
                return null
            }

            setProductsLoadState('loading')
            setProductsError('')

            try {
                const res = await fetch(`${BACKEND_URL}/invoices/all-products`, {
                    method: 'GET',
                    credentials: 'include',
                    signal: controller.signal,
                    headers: {Accept: 'application/json'}
                })

                const raw = await res.json()
                const extracted = extractProductsFromResponse(raw)

                if (!res.ok || !extracted.success) {
                    throw new Error(extracted.message || `HTTP ${res.status}`)
                }

                const normalized = normalizeProducts(extracted.data)
                setProducts(normalized)
                setProductsLoadState('loaded')
                return normalized
            } catch (err) {
                if ((err as any)?.name === 'AbortError') return null
                setProductsLoadState('error')
                setProductsError((err as Error).message || 'Produkte konnten nicht geladen werden')
                return null
            }
        }

        const loadNextInvoiceId = async () => {
            setInvoiceIdLoadState('loading')
            setInvoiceIdError('')

            try {
                const res = await fetch(`${BACKEND_URL}/invoices/next-invoice-id`, {
                    method: 'GET',
                    credentials: 'include',
                    signal: controller.signal,
                    headers: {Accept: 'application/json'}
                })

                const raw = await res.json()
                const extracted = extractInvoiceIdFromResponse(raw)

                if (!res.ok || !extracted.success || extracted.next_invoice_id === null) {
                    throw new Error(extracted.message || `HTTP ${res.status}`)
                }

                const nextId = extracted.next_invoice_id
                setInvoiceId(nextId)
                setForm((prev) => ({...prev, invoiceSequence: nextId}))
                setInvoiceIdLoadState('loaded')
            } catch (err) {
                if ((err as any)?.name === 'AbortError') return
                setInvoiceIdLoadState('error')
                setInvoiceIdError((err as Error).message || 'Invoice ID konnte nicht geladen werden')
            }
        }

        const loadInvoiceForEdit = async (loadedProducts: BackendProduct[]) => {
                if (!editingId || editingId.trim().length === 0) {
                    setInvoiceIdLoadState('error')
                    setInvoiceIdError('Keine invoiceId in der URL gefunden')
                    return
                }

                setInvoiceIdLoadState('loading')
                setInvoiceIdError('')

                try {
                    const res = await fetch(`${BACKEND_URL}/invoices/get-invoice/${editingId}`, {
                        method: 'GET',
                        credentials: 'include',
                        signal: controller.signal,
                        headers: {Accept: 'application/json'}
                    })

                    const raw = await res.json()
                    const extracted = extractGetInvoiceFromResponse(raw)

                    if (!res.ok || !extracted.success || !extracted.data) {
                        throw new Error(extracted.message || `HTTP ${res.status}`)
                    }

                    const inv = extracted.data.invoice
                    const rec = extracted.data.recipient_address
                    const del = extracted.data.delivery_address
                    const lines = Array.isArray(extracted.data.products) ? extracted.data.products : []

                    const nextIssuer: Issuer =
                        typeof inv?.author === 'string' && inv.author.includes('Familienkost') ? 'gmbh' : 'verlag'

                    setIssuer(nextIssuer)

                    const defaultsActive =
                        nextIssuer === 'verlag' &&
                        String(rec?.recipient ?? '') === VERLAG_DEFAULTS.recipient.line1 &&
                        String(rec?.department ?? '') === VERLAG_DEFAULTS.recipient.line2 &&
                        String(rec?.street_address ?? '') === VERLAG_DEFAULTS.recipient.street &&
                        String(rec?.postal_code ?? '') === VERLAG_DEFAULTS.recipient.zip &&
                        String(rec?.city ?? '') === VERLAG_DEFAULTS.recipient.city &&
                        String(rec?.country_code ?? '') === VERLAG_DEFAULTS.recipient.country &&
                        String(rec?.digital_contact ?? '') === VERLAG_DEFAULTS.recipient.contactEmail &&
                        String(del?.recipient ?? '') === VERLAG_DEFAULTS.delivery.line1 &&
                        String(del?.department ?? '') === VERLAG_DEFAULTS.delivery.line2 &&
                        String(del?.street_address ?? '') === VERLAG_DEFAULTS.delivery.street &&
                        String(del?.postal_code ?? '') === VERLAG_DEFAULTS.delivery.zip &&
                        String(del?.city ?? '') === VERLAG_DEFAULTS.delivery.city &&
                        String(del?.country_code ?? '') === VERLAG_DEFAULTS.delivery.country &&
                        String(inv?.mailing_mail ?? '') === VERLAG_DEFAULTS.sendEmail

                    setZeitfrachtDefaultsActive(defaultsActive)

                    const built = buildInitialState(nextIssuer)

                    setInvoiceId(Number(inv.id))
                    setForm({
                        ...built.form,
                        invoiceSequence: Number(inv.id),

                        recipient: {
                            line1: String(rec?.recipient ?? ''),
                            line2: String(rec?.department ?? ''),
                            street: String(rec?.street_address ?? ''),
                            zip: String(rec?.postal_code ?? ''),
                            city: String(rec?.city ?? ''),
                            country: (String(rec?.country_code ?? 'DE') as CountryCode) || 'DE',
                            contactEmail: String(rec?.digital_contact ?? '')
                        },

                        delivery: {
                            line1: String(del?.recipient ?? ''),
                            line2: String(del?.department ?? ''),
                            street: String(del?.street_address ?? ''),
                            zip: String(del?.postal_code ?? ''),
                            city: String(del?.city ?? ''),
                            country: (String(del?.country_code ?? 'DE') as CountryCode) || 'DE'
                        },

                        orderNumber: inv?.order_id != null ? String(inv.order_id) : '',
                        orderDate: inv?.order_date != null ? String(inv.order_date) : '',
                        invoiceDate: inv?.invoice_date != null ? String(inv.invoice_date) : '',
                        weightKg: inv?.weight != null ? String(Number(inv.weight) / 1000) : '',
                        dhlTrackingNumber: inv?.tracking_number != null ? String(inv.tracking_number) : '',
                        sendEmail: String(inv?.mailing_mail ?? ''),
                        sendSubject: String(inv?.mailing_subject ?? ''),
                        sendText: String(inv?.mailing_text ?? '')
                    })

                    if (nextIssuer === 'verlag') {
                        const qtyByProductId = new Map<number, { amount: number; discount: number | null }>()
                        for (const l of lines) {
                            const pid = Number(l?.product_id)
                            if (!Number.isFinite(pid) || pid <= 0) continue
                            qtyByProductId.set(pid, {
                                amount: Number(l?.amount ?? 0),
                                discount: l?.discount == null ? null : Number(l.discount)
                            })
                        }

                        setVerlagOnlySelected(false)
                        const base = buildInitialVerlagLines(loadedProducts)
                        setVerlagLines(
                            base.map((x) => {
                                const hit = qtyByProductId.get(x.productId)
                                if (!hit) return {...x, quantity: '0', discountTouched: false}
                                return {
                                    ...x,
                                    quantity: String(hit.amount ?? 0),
                                    discountPercent: hit.discount == null ? x.discountPercent : String(hit.discount),
                                    discountTouched: hit.discount != null
                                }
                            })
                        )
                    } else {
                        const gLines: GmbhLine[] = lines.map((l: any) => {
                            const p = l?.product
                            return {
                                id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                                name: String(p?.name ?? ''),
                                information: String(p?.information ?? ''),
                                price: p?.price != null ? String(p.price) : ''
                            }
                        })

                        setGmbhLines(gLines.length > 0 ? gLines : buildInitialGmbhLines())
                    }

                    setInvoiceIdLoadState('loaded')
                    setSaveState('saved')
                    setSaveMessage('Gespeichert')
                    setInvalidPaths(new Set())
                } catch (err) {
                    if ((err as any)?.name === 'AbortError') return
                    setInvoiceIdLoadState('error')
                    setInvoiceIdError((err as Error).message || 'Invoice konnte nicht geladen werden')
                }
            }

        ;(async () => {
            const loadedProducts = await loadProducts()
            if (controller.signal.aborted) return
            if (!loadedProducts) return

            if (isCreation) {
                await loadNextInvoiceId()
            } else {
                await loadInvoiceForEdit(loadedProducts)
            }
        })()

        return () => controller.abort()
    }, [isCreation, editingId])

    useEffect(() => {
        if (issuer !== 'verlag') return
        if (productsLoadState !== 'loaded') return

        setVerlagLines((prev) => {
            if (prev.length > 0) return prev
            return buildInitialVerlagLines(products)
        })
    }, [issuer, productsLoadState, products])

    useEffect(() => {
        const hasValidProducts =
            issuer === 'verlag'
                ? verlagLines.some((l) => Math.floor(parseNumberFromInput(l.quantity)) > 0)
                : gmbhLines.some(isValidGmbhLine)

        if (!hasValidProducts) return

        setInvalidPaths((prev) => {
            if (!prev.has('products')) return prev
            const next = new Set(prev)
            next.delete('products')
            return next
        })
    }, [issuer, verlagLines, gmbhLines])

    const invoiceNumber = useMemo(() => {
        return formatInvoiceNumber(issuer, form.invoiceSequence)
    }, [issuer, form.invoiceSequence])

    const isKolibri360 =
        issuer === 'verlag' && form.recipient.line1.trim() === 'Kolibri 360 GmbH'

    const replacePlaceholders = (text: string) => {
        const orderNumber = form.orderNumber?.trim() ?? ''
        const orderDate = form.orderDate?.trim() ?? ''

        return text
            .replaceAll('[Bestellungsnummer]', orderNumber)
            .replaceAll('[Bestellungsdatum]', orderDate)
    }

    const normalizeLineBreaks = (text: string) => {
        return text.replace(/\r?\n/g, '\\n')
    }

    const productsById = useMemo(() => {
        const map = new Map<number, BackendProduct>()
        for (const p of products) {
            map.set(p.id, p)
        }
        return map
    }, [products])

    const publisherProducts = useMemo(() => {
        return products.filter((p) => p.list_for_publisher)
    }, [products])

    const navigate = useNavigate()

    const verlagTotals = useMemo(() => {
        let netSum = 0

        for (const line of verlagLines) {
            const product = productsById.get(line.productId)
            if (!product) continue

            const unitNet = Math.max(0, product.price)
            const qty = Math.max(0, Math.floor(parseNumberFromInput(line.quantity)))
            const netPrice = round2(unitNet * qty)

            const discount = clamp(parseNumberFromInput(line.discountPercent), 0, 100)
            const amount = round2(netPrice * (1 - discount / 100))

            netSum = round2(netSum + amount)
        }

        const vat = round2(netSum * PUBLISHER_VAT)
        const gross = round2(netSum + vat)

        return {netSum, vat, gross}
    }, [verlagLines, productsById])

    const gmbhTotals = useMemo(() => {
        let netSum = 0

        for (const line of gmbhLines) {
            const price = Math.max(0, parseNumberFromInput(line.price))
            netSum = round2(netSum + price)
        }

        const vat = round2(netSum * GMBH_VAT)
        const gross = round2(netSum + vat)

        return {netSum, vat, gross}
    }, [gmbhLines])

    const selectedVerlagLines = useMemo(() => {
        if (issuer !== 'verlag') return []

        return verlagLines
            .map((line) => {
                const product = productsById.get(line.productId)
                if (!product) return null

                const qty = Math.max(0, Math.floor(parseNumberFromInput(line.quantity)))
                if (qty <= 0) return null

                const unitNet = Math.max(0, product.price)
                const netPrice = round2(unitNet * qty)

                const discountPercent = clamp(parseNumberFromInput(line.discountPercent), 0, 100)
                const amountNet = round2(netPrice * (1 - discountPercent / 100))

                return {
                    productId: product.id,
                    qty,
                    netPrice, // Netto vor Rabatt
                    amountNet, // Netto nach Rabatt
                }
            })
            .filter((x): x is NonNullable<typeof x> => Boolean(x))
    }, [issuer, verlagLines, productsById])

    const verlagStats = useMemo(() => {
        if (issuer !== 'verlag') {
            return {avgDiscount: 0, totalQty: 0, distinctTitles: 0}
        }

        const distinctTitles = selectedVerlagLines.length
        const totalQty = selectedVerlagLines.reduce((sum, l) => sum + l.qty, 0)

        const totalNetBeforeDiscount = selectedVerlagLines.reduce((sum, l) => sum + l.netPrice, 0)
        const totalNetAfterDiscount = selectedVerlagLines.reduce((sum, l) => sum + l.amountNet, 0)

        const avgDiscount =
            totalNetBeforeDiscount > 0
                ? round2((1 - totalNetAfterDiscount / totalNetBeforeDiscount) * 100)
                : 0

        return {avgDiscount, totalQty, distinctTitles}
    }, [issuer, selectedVerlagLines])

    const gmbhStats = useMemo(() => {
        if (issuer !== 'gmbh') {
            return {positions: 0, netSoFar: 0}
        }

        const positions = gmbhLines.reduce((sum, l) => {
            const priceNet = Math.max(0, parseNumberFromInput(l.price))
            const hasContent =
                l.name.trim().length > 0 || l.information.trim().length > 0 || priceNet > 0

            return sum + (hasContent ? 1 : 0)
        }, 0)

        return {positions, netSoFar: gmbhTotals.netSum}
    }, [issuer, gmbhLines, gmbhTotals.netSum])

    const copyRecipientToDelivery = () => {
        markInvalid()

        setForm((prev) => ({
            ...prev,
            delivery: {
                line1: prev.recipient.line1,
                line2: prev.recipient.line2,
                street: prev.recipient.street,
                zip: prev.recipient.zip,
                city: prev.recipient.city,
                country: prev.recipient.country,
            },
        }))

        setSaveState('unsaved')
        setSaveMessage('Änderungen vorhanden (noch nicht gespeichert)')
    }

    const fieldClass = (path: string) => {
        const invalid = invalidPaths.has(path)
        return `creationInput ${invalid ? 'creationInputInvalid' : ''}`
    }

    const selectClass = (path: string) => {
        const invalid = invalidPaths.has(path)
        return `creationSelect ${invalid ? 'creationInputInvalid' : ''}`
    }

    const textareaClass = (path: string) => {
        const invalid = invalidPaths.has(path)
        return `creationTextarea ${invalid ? 'creationInputInvalid' : ''}`
    }

    const isValidGmbhLine = (line: GmbhLine) => {
        const nameOk = line.name.trim().length > 0
        const priceNet = parseNumberFromInput(line.price)
        const priceOk = Number.isFinite(priceNet) && priceNet > 0
        return nameOk && priceOk
    }


    const markInvalid = (path?: string) => {
        if (saveState === 'saved') {
            setSaveState('unsaved')
            setSaveMessage('Änderungen vorhanden (noch nicht gespeichert)')
        }

        if (path) {
            setInvalidPaths((prev) => {
                if (!prev.has(path)) return prev
                const next = new Set(prev)
                next.delete(path)
                return next
            })
        }
    }

    const resetProductsForIssuer = (next: Issuer) => {
        setVerlagOnlySelected(false)

        if (next === 'verlag') {
            if (productsLoadState === 'loaded') {
                setVerlagLines(buildInitialVerlagLines(products))
            } else {
                setVerlagLines([])
            }
        } else {
            setGmbhLines(buildInitialGmbhLines())
        }
    }

    const switchIssuer = (next: Issuer) => {
        setIssuer(next)
        const built = buildInitialState(next)

        setForm((prev) => ({
            ...built.form,
            invoiceSequence: prev.invoiceSequence, // behalten
        }))

        setZeitfrachtDefaultsActive(built.defaultsActive)
        resetProductsForIssuer(next)

        setSaveState('unsaved')
        setSaveMessage('Noch nicht gespeichert')
        setInvalidPaths(new Set())
        setIsSendModalOpen(false)
    }

    const clearZeitfrachtDefaults = (
        state: FormState,
        changed:
            | { section: 'recipient'; field: keyof RecipientAddress }
            | { section: 'delivery'; field: keyof DeliveryAddress }
            | { section: 'sendEmail' }
    ): FormState => {
        const next: FormState = {
            ...state,
            recipient: {...state.recipient},
            delivery: {...state.delivery},
        }

        const recipientKeys: (keyof RecipientAddress)[] = [
            'line1',
            'line2',
            'street',
            'zip',
            'city',
            'country',
            'contactEmail',
        ]

        const deliveryKeys: (keyof DeliveryAddress)[] = [
            'line1',
            'line2',
            'street',
            'zip',
            'city',
            'country',
        ]

        for (const key of recipientKeys) {
            const isChanged = changed.section === 'recipient' && changed.field === key
            if (isChanged) continue

            const defaultValue = VERLAG_DEFAULTS.recipient[key]
            if (next.recipient[key] === defaultValue) {
                next.recipient[key] = ''
            }
        }

        for (const key of deliveryKeys) {
            const isChanged = changed.section === 'delivery' && changed.field === key
            if (isChanged) continue

            const defaultValue = VERLAG_DEFAULTS.delivery[key]
            if (next.delivery[key] === defaultValue) {
                next.delivery[key] = ''
            }
        }

        const sendEmailChanged = changed.section === 'sendEmail'
        if (!sendEmailChanged && next.sendEmail === VERLAG_DEFAULTS.sendEmail) {
            next.sendEmail = ''
        }

        return next
    }

    const updateRecipient = (field: keyof RecipientAddress, value: string) => {
        markInvalid(`recipient.${field}`)

        const breaksDefaults =
            issuer === 'verlag' &&
            zeitfrachtDefaultsActive &&
            value !== VERLAG_DEFAULTS.recipient[field]

        if (breaksDefaults) {
            setZeitfrachtDefaultsActive(false)
        }

        setForm((prev) => {
            let next: FormState = {
                ...prev,
                recipient: {
                    ...prev.recipient,
                    [field]: value,
                },
            }

            if (breaksDefaults) {
                next = clearZeitfrachtDefaults(next, {section: 'recipient', field})
            }

            return next
        })
    }

    const updateDelivery = (field: keyof DeliveryAddress, value: string) => {
        markInvalid(`delivery.${field}`)

        const breaksDefaults =
            issuer === 'verlag' &&
            zeitfrachtDefaultsActive &&
            value !== VERLAG_DEFAULTS.delivery[field]

        if (breaksDefaults) {
            setZeitfrachtDefaultsActive(false)
        }

        setForm((prev) => {
            let next: FormState = {
                ...prev,
                delivery: {
                    ...prev.delivery,
                    [field]: value,
                },
            }

            if (breaksDefaults) {
                next = clearZeitfrachtDefaults(next, {section: 'delivery', field})
            }

            return next
        })
    }

    const updateSendEmail = (value: string) => {
        markInvalid('sendEmail')

        const breaksDefaults =
            issuer === 'verlag' && zeitfrachtDefaultsActive && value !== VERLAG_DEFAULTS.sendEmail

        if (breaksDefaults) {
            setZeitfrachtDefaultsActive(false)
        }

        setForm((prev) => {
            let next: FormState = {
                ...prev,
                sendEmail: value,
            }

            if (breaksDefaults) {
                next = clearZeitfrachtDefaults(next, {section: 'sendEmail'})
            }

            return next
        })
    }

    const updateSendSubject = (value: string) => {
        markInvalid('sendSubject')

        setForm((prev) => {
            let next: FormState = {...prev, sendSubject: value}
            return next
        })
    }

    const updateVerlagQuantity = (productId: number, value: string) => {
        markInvalid('products')
        setVerlagLines((prev) =>
            prev.map((l) => (l.productId === productId ? {...l, quantity: value} : l))
        )
    }

    const updateVerlagDiscount = (productId: number, value: string) => {
        markInvalid('products')
        setVerlagLines((prev) =>
            prev.map((l) =>
                l.productId === productId
                    ? {...l, discountPercent: value, discountTouched: true}
                    : l
            )
        )
    }

    const updateGmbhLine = (id: string, patch: Partial<Omit<GmbhLine, 'id'>>) => {
        markInvalid('products')
        setGmbhLines((prev) => prev.map((l) => (l.id === id ? {...l, ...patch} : l)))
    }

    const addGmbhLine = () => {
        markInvalid('products')
        setGmbhLines((prev) => [...prev, newGmbhLine()])
    }

    const buildProductsPayload = () => {
        if (issuer === 'verlag') {
            const lines = verlagLines
                .map((line) => {
                    const product = productsById.get(line.productId)
                    if (!product) return null

                    const unitNet = Math.max(0, product.price)
                    const qty = Math.max(0, Math.floor(parseNumberFromInput(line.quantity)))
                    const netPrice = round2(unitNet * qty)

                    const discount = clamp(parseNumberFromInput(line.discountPercent), 0, 100)
                    const amount = round2(netPrice * (1 - discount / 100))

                    return {
                        productId: product.id,
                        name: product.name,
                        isbn: product.isbn_number,
                        unitNet,
                        quantity: qty,
                        netPrice,
                        discountPercent: discount,
                        amountNet: amount,
                    }
                })
                .filter((x): x is NonNullable<typeof x> => Boolean(x))
                .filter((x) => x.quantity > 0)

            return {
                mode: 'verlag' as const,
                vatRate: PUBLISHER_VAT,
                vatAmount: verlagTotals.vat,
                grossTotal: verlagTotals.gross,
                lines,
            }
        }

        const lines = gmbhLines
            .map((l) => {
                const price = Math.max(0, parseNumberFromInput(l.price))
                return {
                    id: l.id,
                    name: l.name.trim(),
                    information: l.information.trim(),
                    priceNet: price,
                }
            })
            .filter((l) => l.name.length > 0 || l.information.length > 0 || l.priceNet > 0)

        return {
            mode: 'gmbh' as const,
            vatRate: GMBH_VAT,
            vatAmount: gmbhTotals.vat,
            grossTotal: gmbhTotals.gross,
            lines,
        }
    }

    const doSave = async () => {
        const invalid = validateForm(form, issuer)

        if (issuer === 'verlag' && !hasAnyVerlagProduct()) invalid.add('products')
        if (issuer === 'gmbh' && !hasAnyValidGmbhProduct()) invalid.add('products')

        setInvalidPaths(invalid)

        if (invalid.size > 0) {
            setSaveState('error')
            console.log(invalid)
            setSaveMessage('Bitte markierte Pflichtfelder ausfüllen')
            return
        }

        if (!BACKEND_URL || BACKEND_URL.trim().length === 0) {
            setSaveState('error')
            setSaveMessage('BACKEND_URL fehlt (REACT_APP_BACKEND_URL).')
            return
        }

        if (!isCreation && (!editingId || editingId.trim().length === 0)) {
            setSaveState('error')
            setSaveMessage('Keine editingId in der URL gefunden')
            return
        }

        const author =
            issuer === 'verlag' ? 'Terence Böhme' : 'Familienkost GmbH'

        const year = new Date().getFullYear()

        const productsPayload =
            issuer === 'verlag'
                ? verlagLines
                    .map((line) => {
                        const product = productsById.get(line.productId)
                        if (!product) return null

                        const amount = Math.max(0, Math.floor(parseNumberFromInput(line.quantity)))
                        if (amount <= 0) return null

                        const discount = clamp(parseNumberFromInput(line.discountPercent), 0, 100)

                        return {
                            name: product.name,
                            information: product.information ?? undefined,
                            isbn_number: product.isbn_number ?? undefined,
                            price: Number(product.price),
                            discount: Number.isFinite(discount) ? Math.round(discount) : undefined,
                            amount
                        }
                    })
                    .filter((x): x is NonNullable<typeof x> => Boolean(x))
                : gmbhLines
                    .filter(isValidGmbhLine)
                    .map((l) => ({
                        name: l.name.trim(),
                        information: l.information.trim().length > 0 ? l.information.trim() : undefined,
                        isbn_number: undefined,
                        price: parseNumberFromInput(l.price),
                        discount: undefined,
                        amount: 1
                    }))

        const payload = {
            id: isCreation ? form.invoiceSequence : Number(editingId),
            year,
            author,

            recipient_address: {
                recipient: form.recipient.line1,
                department: form.recipient.line2 || undefined,
                street_address: form.recipient.street,
                postal_code: form.recipient.zip,
                city: form.recipient.city,
                country_code: form.recipient.country,
                digital_contact: form.recipient.contactEmail || undefined
            },

            mailing_mail: form.sendEmail,
            mailing_subject: form.sendSubject,
            mailing_text: form.sendText,

            delivery_address:
                issuer === 'verlag'
                    ? {
                        recipient: form.delivery.line1,
                        department: form.delivery.line2 || undefined,
                        street_address: form.delivery.street,
                        postal_code: form.delivery.zip,
                        city: form.delivery.city,
                        country_code: form.delivery.country,
                        digital_contact: undefined
                    }
                    : undefined,

            order_id: form.orderNumber.trim().length > 0 ? form.orderNumber.trim() : undefined,
            order_date: form.orderDate.trim().length > 0 ? form.orderDate.trim() : undefined,
            weight: form.weightKg.trim().length > 0 ? Math.round(parseNumberFromInput(form.weightKg) * 1000) : undefined,
            tracking_number: form.dhlTrackingNumber.trim().length > 0 ? form.dhlTrackingNumber.trim() : undefined,

            products: productsPayload
        }

        const url = isCreation
            ? `${BACKEND_URL}/invoices/save-invoice`
            : `${BACKEND_URL}/invoices/update-invoice/${editingId}`

        try {
            setSaveState('unsaved')
            setSaveMessage('Speichern…')

            const res = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })

            const raw = await res.json()

            const ok =
                raw &&
                typeof raw === 'object' &&
                (raw.success === true || raw?.body?.success === true)

            const message =
                typeof raw?.message === 'string'
                    ? raw.message
                    : typeof raw?.body?.message === 'string'
                        ? raw.body.message
                        : ''

            if (!res.ok || !ok) {
                throw new Error(message || `HTTP ${res.status}`)
            }

            setSaveState('saved')
            setSaveMessage('Gespeichert')

            if (isCreation) {
                const newId = payload.id
                navigate(`/editingpage/${newId}`, {replace: true})
            }
        } catch (err) {
            setSaveState('error')
            setSaveMessage((err as Error).message || 'Speichern fehlgeschlagen')
        }
    }

    const openSendModal = () => {
        if (saveState !== 'saved') {
            setSaveState('error')
            setSaveMessage('Bitte zuerst speichern')
            return
        }

        setSendConfirmOpen(false)
        setIsSendModalOpen(true)
    }

    const closeSendModal = () => {
        setIsSendModalOpen(false)
        setSendConfirmOpen(false)
    }

    const doOpenLieferschein = () => {
        closeSendModal()
        window.open(
            `${BACKEND_URL}/invoices/delivery-note/${invoiceId}`,
            '_blank'
        )
    }

    const doOpenPdf = () => {
        closeSendModal()

        window.open(
            `${BACKEND_URL}/invoices/invoice/${invoiceId}`,
            '_blank'
        )
    }

    const requestSendConfirmation = () => {
        setSendConfirmOpen(true)
    }

    const doReallySend = async () => {
        if (!invoiceId) {
            setSendError('Keine invoiceId vorhanden')
            return
        }

        setIsSending(true)
        setSendError(null)

        try {
            const res = await fetch(`${BACKEND_URL}/invoices/send-invoice-mail`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    invoice_id: invoiceId,
                    formated_invoice_id: invoiceNumber,
                    to: form.sendEmail,
                    subject: replacePlaceholders(form.sendSubject),
                    text: replacePlaceholders(form.sendText),
                }),
            })

            const raw = await res.json()

            if (!res.ok || raw?.success === false) {
                throw new Error(raw?.message || `HTTP ${res.status}`)
            }

            setSendConfirmOpen(false)
            closeSendModal()
        } catch (err) {
            setSendError((err as Error).message || 'Mailversand fehlgeschlagen')
        } finally {
            setIsSending(false)
        }
    }

    const canSend = saveState === 'saved'

    return (
        <main className='creationPage'>
            <div className='creationCard'>
                <header className='creationHeader'>
                    <div className='creationHeaderLeft'>
                        <img
                            className='creationLogo'
                            src='https://www.familienkost.de/icon-svgs/logos/Familienkost_Logo_Horizontal_Farbig.svg'
                            alt='Familienkost Logo'
                        />
                        <div className='creationTitleBlock'>
                            <h1 className='creationTitle'>
                                Rechnung {isCreation ? 'erstellen' : 'bearbeiten'}
                            </h1>
                            <div className='creationTitleRow'>
                                <button
                                    type='button'
                                    className='backLink'
                                    onClick={() => navigate(-1)}
                                    aria-label='Zurück'
                                >
                                    ← Zurück
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className='creationHeaderRight'>
                        {isCreation &&
                            <div className='issuerSwitch' role='tablist' aria-label='Aussteller'>
                                <button
                                    type='button'
                                    className={`issuerSwitchBtn ${
                                        issuer === 'verlag' ? 'issuerSwitchBtnActive' : ''
                                    }`}
                                    onClick={() => switchIssuer('verlag')}
                                >
                                    Verlag Terence Böhme
                                </button>
                                <button
                                    type='button'
                                    className={`issuerSwitchBtn ${
                                        issuer === 'gmbh' ? 'issuerSwitchBtnActive' : ''
                                    }`}
                                    onClick={() => switchIssuer('gmbh')}
                                >
                                    Familienkost GmbH
                                </button>
                            </div>
                        }

                        <div className='invoiceNumberBox'>
                            <div className='invoiceNumberLabel'>Rechnungsnummer</div>

                            {invoiceIdLoadState === 'loading' && (
                                <div className='invoiceNumberLoading'>Lädt…</div>
                            )}

                            {invoiceIdLoadState === 'error' && (
                                <div className='productsError'>{invoiceIdError}</div>
                            )}

                            {invoiceIdLoadState === 'loaded' && invoiceId !== null && (
                                <input
                                    className='creationInput creationInputReadonly'
                                    value={invoiceNumber}
                                    readOnly
                                />
                            )}
                        </div>
                    </div>
                </header>

                <div className='creationLayoutTop'>
                    <div className='creationCol'>
                        <section className='creationSection'>
                            <div className='sectionHeader'>
                                <h2>Empfängeradresse</h2>
                                {issuer === 'verlag' && zeitfrachtDefaultsActive && (
                                    <span className='sectionBadge'>Defaults aktiv</span>
                                )}
                            </div>

                            <div className='creationGrid'>
                                <div className='creationField'>
                                    <label className='creationLabelRow'>
                                        <span>Empfängernamenszeile 1</span>
                                    </label>
                                    <input
                                        className={fieldClass('recipient.line1')}
                                        value={form.recipient.line1}
                                        onChange={(e) => updateRecipient('line1', e.target.value)}
                                        placeholder='z.B. Kolibri 360 GmbH'
                                    />
                                </div>

                                <div className='creationField'>
                                    <label className='creationLabelRow'>
                                        <span>Empfängernamenszeile 2</span>
                                        <span className='creationOptional'>optional</span>
                                    </label>
                                    <input
                                        className='creationInput'
                                        value={form.recipient.line2}
                                        onChange={(e) => updateRecipient('line2', e.target.value)}
                                        placeholder='z.B. Rechnungsprüfung'
                                    />
                                </div>

                                <div className='creationField creationFieldSpan2'>
                                    <label className='creationLabelRow'>
                                        <span>Straße & Hausnummer</span>
                                    </label>
                                    <input
                                        className={fieldClass('recipient.street')}
                                        value={form.recipient.street}
                                        onChange={(e) => updateRecipient('street', e.target.value)}
                                        placeholder='z.B. Industriestraße 23'
                                    />
                                </div>

                                <div className='creationField'>
                                    <label className='creationLabelRow'>
                                        <span>Postleitzahl</span>
                                    </label>
                                    <input
                                        className={fieldClass('recipient.zip')}
                                        value={form.recipient.zip}
                                        onChange={(e) => updateRecipient('zip', e.target.value)}
                                        placeholder='z.B. 70565'
                                    />
                                </div>

                                <div className='creationField'>
                                    <label className='creationLabelRow'>
                                        <span>Stadt</span>
                                    </label>
                                    <input
                                        className={fieldClass('recipient.city')}
                                        value={form.recipient.city}
                                        onChange={(e) => updateRecipient('city', e.target.value)}
                                        placeholder='z.B. Stuttgart'
                                    />
                                </div>

                                <div className='creationField'>
                                    <label className='creationLabelRow'>
                                        <span>Land</span>
                                    </label>
                                    <select
                                        className={selectClass('recipient.country')}
                                        value={form.recipient.country}
                                        onChange={(e) =>
                                            updateRecipient('country', e.target.value as CountryCode)
                                        }
                                    >
                                        <option value=''>Bitte auswählen</option>
                                        <option value='DE'>Deutschland (DE)</option>
                                        <option value='AT'>Österreich (AT)</option>
                                    </select>
                                </div>

                                <div className='creationField'>
                                    <label className='creationLabelRow'>
                                        <span>Digitaler Kontakt</span>
                                        <span className='creationOptional'>optional</span>
                                    </label>
                                    <input
                                        className='creationInput'
                                        type='email'
                                        value={form.recipient.contactEmail}
                                        onChange={(e) =>
                                            updateRecipient('contactEmail', e.target.value)
                                        }
                                        placeholder='z.B. invoice@kolibri360.de'
                                    />
                                </div>
                            </div>

                            {issuer === 'verlag' && (
                                <p className='creationInfo'>
                                    Hinweis: Sobald du einen Kolibri360-Default änderst, werden die
                                    übrigen Defaults automatisch geleert.
                                </p>
                            )}
                        </section>

                        {issuer === 'verlag' && (
                            <section className='creationSection'>
                                <div className='sectionHeader'>
                                    <h2>Lieferadresse</h2>

                                    <button
                                        type='button'
                                        className='copyBtn'
                                        onClick={copyRecipientToDelivery}
                                    >
                                        Rechnungsadresse kopieren
                                    </button>
                                </div>

                                <div className='creationGrid'>
                                    <div className='creationField'>
                                        <label className='creationLabelRow'>
                                            <span>Empfängernamenszeile 1</span>
                                        </label>
                                        <input
                                            className={fieldClass('delivery.line1')}
                                            value={form.delivery.line1}
                                            onChange={(e) => updateDelivery('line1', e.target.value)}
                                            placeholder='z.B. Kolibri 360 GmbH'
                                        />
                                    </div>

                                    <div className='creationField'>
                                        <label className='creationLabelRow'>
                                            <span>Empfängernamenszeile 2</span>
                                            <span className='creationOptional'>optional</span>
                                        </label>
                                        <input
                                            className='creationInput'
                                            value={form.delivery.line2}
                                            onChange={(e) => updateDelivery('line2', e.target.value)}
                                            placeholder='z.B. Kolibri360 Barsortiment 20008'
                                        />
                                    </div>

                                    <div className='creationField creationFieldSpan2'>
                                        <label className='creationLabelRow'>
                                            <span>Straße & Hausnummer</span>
                                        </label>
                                        <input
                                            className={fieldClass('delivery.street')}
                                            value={form.delivery.street}
                                            onChange={(e) =>
                                                updateDelivery('street', e.target.value)
                                            }
                                            placeholder='z.B. Ferdinand-Jühlke-Strasse 7'
                                        />
                                    </div>

                                    <div className='creationField'>
                                        <label className='creationLabelRow'>
                                            <span>Postleitzahl</span>
                                        </label>
                                        <input
                                            className={fieldClass('delivery.zip')}
                                            value={form.delivery.zip}
                                            onChange={(e) => updateDelivery('zip', e.target.value)}
                                            placeholder='z.B. 99095'
                                        />
                                    </div>

                                    <div className='creationField'>
                                        <label className='creationLabelRow'>
                                            <span>Stadt</span>
                                        </label>
                                        <input
                                            className={fieldClass('delivery.city')}
                                            value={form.delivery.city}
                                            onChange={(e) => updateDelivery('city', e.target.value)}
                                            placeholder='z.B. Erfurt'
                                        />
                                    </div>

                                    <div className='creationField'>
                                        <label className='creationLabelRow'>
                                            <span>Land</span>
                                        </label>
                                        <select
                                            className={selectClass('delivery.country')}
                                            value={form.delivery.country}
                                            onChange={(e) =>
                                                updateDelivery('country', e.target.value as CountryCode)
                                            }
                                        >
                                            <option value=''>Bitte auswählen</option>
                                            <option value='DE'>Deutschland (DE)</option>
                                            <option value='AT'>Österreich (AT)</option>
                                        </select>
                                    </div>
                                </div>
                            </section>
                        )}


                        {issuer === 'gmbh' && (
                            <section className='creationSection'>
                                <div className='sectionHeader'>
                                    <h2>Statistik</h2>
                                </div>

                                <div className='microStatsGrid'>
                                    <div className='microStatsItem'>
                                        <div className='microStatsLabel'>Positionen</div>
                                        <div className='microStatsValue'>
                                            {gmbhStats.positions}
                                        </div>
                                    </div>

                                    <div className='microStatsItem'>
                                        <div className='microStatsLabel'>Netto bisher</div>
                                        <div className='microStatsValue'>
                                            {formatEuro(gmbhStats.netSoFar)}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>

                    <div className='creationCol'>
                        <section className='creationSection'>
                            <div className='sectionHeader'>
                                <h2>Details</h2>
                            </div>

                            <div className='creationGrid'>
                                <div className='creationField creationFieldSpan2'>
                                    <label className='creationLabelRow'>
                                        <span>Bestellungsnummer</span>
                                        <span className='creationOptional'>optional</span>
                                    </label>
                                    <input
                                        className='creationInput'
                                        value={form.orderNumber}
                                        onChange={(e) => {
                                            markInvalid()
                                            setForm((prev) => ({
                                                ...prev,
                                                orderNumber: e.target.value,
                                            }))
                                        }}
                                        placeholder='z.B. PO-12345'
                                    />
                                </div>

                                <div className='creationField creationFieldSpan2'>
                                    <label className='creationLabelRow'>
                                        <span>Bestelldatum</span>
                                        <span className='creationOptional'>optional</span>
                                    </label>
                                    <input
                                        className='creationInput'
                                        value={form.orderDate}
                                        onChange={(e) => {
                                            markInvalid()
                                            setForm((prev) => ({
                                                ...prev,
                                                orderDate: e.target.value,
                                            }))
                                        }}
                                        placeholder='z.B. 17. Mai 2026'
                                    />
                                </div>

                                {issuer === 'verlag' && (
                                    <>
                                        <div className='creationField creationFieldSpan2'>
                                            <label className='creationLabelRow'>
                                                <span>DHL-Sendungsnummer</span>
                                                {!isKolibri360 && (
                                                    <span className='creationOptional'>optional</span>
                                                )}
                                            </label>
                                            <input
                                                className={fieldClass('dhlTrackingNumber')}
                                                value={form.dhlTrackingNumber}
                                                onChange={(e) => {
                                                    markInvalid('dhlTrackingNumber')
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        dhlTrackingNumber: e.target.value,
                                                    }))
                                                }}
                                                placeholder='z.B. 00340434123456789012'
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            {issuer === 'verlag' && !isKolibri360 && (
                                <p className='creationInfo'>
                                    Hinweis: DHL-Sendungsnummer ist nur bei{' '}
                                    <strong>Kolibri 360 GmbH</strong> Pflicht.
                                </p>
                            )}
                        </section>

                        <section className='creationSection'>
                            <div className='sectionHeader'>
                                <h2>Versand</h2>
                            </div>

                            <div className='creationGrid'>
                                <div className='creationField creationFieldSpan2'>
                                    <label className='creationLabelRow'>
                                        <span>Sende-E-Mail</span>
                                    </label>
                                    <input
                                        className={fieldClass('sendEmail')}
                                        type='email'
                                        value={form.sendEmail}
                                        onChange={(e) => updateSendEmail(e.target.value)}
                                        placeholder='z.B. invoice@kolibri360.de'
                                    />
                                </div>

                                <div className='creationField creationFieldSpan2'>
                                    <label className='creationLabelRow'>
                                        <span>Sende Betreff</span>
                                    </label>

                                    <input
                                        className={fieldClass('sendSubject')}
                                        value={form.sendSubject}
                                        onChange={(e) => updateSendSubject(e.target.value)}
                                        placeholder='z.B. Ihre Rechnung zur Bestellung [Bestellungsnummer]'
                                    />
                                </div>

                                <div className='creationField creationFieldSpan2'>
                                    <label className='creationLabelRow'>
                                        <span>Sende Text</span>
                                    </label>
                                    <textarea
                                        className={textareaClass('sendText')}
                                        value={form.sendText}
                                        onChange={(e) => {
                                            markInvalid('sendText')
                                            setForm((prev) => ({
                                                ...prev,
                                                sendText: e.target.value,
                                            }))
                                        }}
                                        placeholder='Text für die E-Mail...'
                                        rows={6}
                                    />
                                </div>

                                <span
                                    className='creationOptional creationFieldSpan2'>Platzhalter: [Bestellungsnummer], [Bestellungsdatum]</span>
                            </div>
                        </section>

                        {issuer === 'verlag' && (
                            <section className='creationSection'>
                                <div className='sectionHeader'>
                                    <h2>Statistik</h2>
                                </div>

                                <div className='microStatsGrid'>
                                    <div className='microStatsItem'>
                                        <div className='microStatsLabel'>Ø Rabatt</div>
                                        <div className='microStatsValue'>
                                            {verlagStats.avgDiscount.toFixed(2)}%
                                        </div>
                                    </div>

                                    <div className='microStatsItem'>
                                        <div className='microStatsLabel'>Gesamtmenge</div>
                                        <div className='microStatsValue'>
                                            {verlagStats.totalQty}
                                        </div>
                                    </div>

                                    <div className='microStatsItem'>
                                        <div className='microStatsLabel'>Titel</div>
                                        <div className='microStatsValue'>
                                            {verlagStats.distinctTitles}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>
                </div>

                <section
                    className={`creationSection creationSectionFull ${
                        invalidPaths.has('products') ? 'creationSectionInvalid' : ''
                    }`}
                >
                    <div className='sectionHeader'>
                        <h2>Produkte</h2>
                        {issuer === 'gmbh' ? (
                            <button
                                type='button'
                                className='addProductBtn'
                                onClick={addGmbhLine}
                                aria-label='Produkt hinzufügen'
                                title='Produkt hinzufügen'
                            >
                                +
                            </button>
                        ) : (
                            <span className='sectionBadge'>vorausgewählt</span>
                        )}
                    </div>

                    {invalidPaths.has('products') && (
                        <>
                            <p className='productsError'>
                                Bitte wählen sie mindestens 1 Produkt aus.
                            </p>
                            <br/>
                        </>
                    )}

                    {issuer === 'verlag' ? (
                        <>
                            {productsLoadState === 'loading' && (
                                <p className='productsHint'>Produkte werden geladen…</p>
                            )}

                            {productsLoadState === 'error' && (
                                <p className='productsError'>
                                    Produkte konnten nicht geladen werden: {productsError}
                                </p>
                            )}

                            {productsLoadState === 'loaded' && publisherProducts.length === 0 && (
                                <p className='productsHint'>
                                    Keine Produkte mit <strong>list_for_publisher</strong> gefunden.
                                </p>
                            )}

                            {productsLoadState === 'loaded' &&
                                publisherProducts.length > 0 &&
                                verlagLines.length === 0 && (
                                    <p className='productsHint'>Produkte werden vorbereitet…</p>
                                )}

                            {productsLoadState === 'loaded' && verlagLines.length > 0 && (
                                <>
                                    <div className='productsToolbar'>
                                        <label className='productsCheckbox'>
                                            <input
                                                type='checkbox'
                                                checked={verlagOnlySelected}
                                                onChange={(e) =>
                                                    setVerlagOnlySelected(e.target.checked)
                                                }
                                            />
                                            Nur Menge &gt; 0 anzeigen
                                        </label>

                                        <span className='productsMiniHint'>
                                            Rabatt-Standard: Brutto &gt; 10€ → 50%, sonst 55%
                                        </span>
                                    </div>

                                    <div className='productTableWrap'>
                                        <table className='productTable'>
                                            <thead>
                                            <tr>
                                                <th className='productColName'>Name</th>
                                                <th className='productColN productNumber'>
                                                    Stückpreis
                                                </th>
                                                <th className='productColN productNumber'>
                                                    Menge
                                                </th>
                                                <th className='productColN productNumber'>
                                                    Netto-Preis
                                                </th>
                                                <th className='productColN productNumber'>
                                                    Rabatt (%)
                                                </th>
                                                <th className='productColN productNumber'>
                                                    Betrag
                                                </th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {verlagLines
                                                .filter((line) => {
                                                    if (!verlagOnlySelected) return true
                                                    const qty = Math.max(
                                                        0,
                                                        Math.floor(
                                                            parseNumberFromInput(line.quantity)
                                                        )
                                                    )
                                                    return qty > 0
                                                })
                                                .map((line) => {
                                                    const product = productsById.get(
                                                        line.productId
                                                    )
                                                    if (!product || !product.list_for_publisher) {
                                                        return null
                                                    }

                                                    const unitNet = Math.max(0, product.price)
                                                    const qty = Math.max(
                                                        0,
                                                        Math.floor(
                                                            parseNumberFromInput(line.quantity)
                                                        )
                                                    )
                                                    const netPrice = round2(unitNet * qty)

                                                    const discount = clamp(
                                                        parseNumberFromInput(line.discountPercent),
                                                        0,
                                                        100
                                                    )
                                                    const amount = round2(
                                                        netPrice * (1 - discount / 100)
                                                    )

                                                    const nameTitle = product.isbn_number
                                                        ? `${product.name} (ISBN: ${product.isbn_number})`
                                                        : product.name

                                                    return (
                                                        <tr
                                                            key={product.id}
                                                            className={
                                                                qty > 0 ? 'productRowActive' : ''
                                                            }
                                                        >
                                                            <td className='productColName'>
                                                                <div
                                                                    className='productName'
                                                                    title={nameTitle}
                                                                >
                                                                    {product.name}
                                                                </div>
                                                            </td>

                                                            <td className='productColN productNumber'>
                                                                <div className='productReadonly'>
                                                                    {formatEuro(unitNet)}
                                                                </div>
                                                            </td>

                                                            <td className='productColN productNumber'>
                                                                <input
                                                                    className='creationInput productInputCompact productNumber'
                                                                    type='number'
                                                                    min='0'
                                                                    step='1'
                                                                    value={line.quantity}
                                                                    onChange={(e) =>
                                                                        updateVerlagQuantity(
                                                                            product.id,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder='0'
                                                                />
                                                            </td>

                                                            <td className='productColN productNumber'>
                                                                <div className='productReadonly'>
                                                                    {formatEuro(netPrice)}
                                                                </div>
                                                            </td>

                                                            <td className='productColN productNumber'>
                                                                <input
                                                                    className='creationInput productInputCompact productNumber'
                                                                    type='number'
                                                                    min='0'
                                                                    max='100'
                                                                    step='0.01'
                                                                    value={line.discountPercent}
                                                                    onChange={(e) =>
                                                                        updateVerlagDiscount(
                                                                            product.id,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder={defaultPublisherDiscount(
                                                                        unitNet
                                                                    )}
                                                                />
                                                            </td>

                                                            <td className='productColN productNumber'>
                                                                <div className='productReadonly'>
                                                                    {formatEuro(amount)}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className='productTotals'>
                                        <div className='productTotalsRow'>
                                            <div className='productTotalsLabel'>
                                                gesetzl. MwSt 7%
                                            </div>
                                            <div className='productTotalsValue'>
                                                {formatEuro(verlagTotals.vat)}
                                            </div>
                                        </div>

                                        <div className='productTotalsRow productTotalsRowStrong'>
                                            <div className='productTotalsLabel'>Brutto-Preis</div>
                                            <div className='productTotalsValue'>
                                                {formatEuro(verlagTotals.gross)}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className='productsList'>
                                {gmbhLines.map((line) => (
                                    <div className='productBox' key={line.id}>
                                        <div className='creationGrid'>
                                            <div className='creationField'>
                                                <label className='creationLabelRow'>
                                                    <span>Beschreibung</span>
                                                </label>
                                                <textarea
                                                    className='creationTextarea'
                                                    value={line.name}
                                                    onChange={(e) =>
                                                        updateGmbhLine(line.id, {
                                                            name: e.target.value,
                                                        })
                                                    }
                                                    placeholder='z.B. Dienstleistung'
                                                    rows={3}
                                                />
                                            </div>

                                            <div className='creationField'>
                                                <label className='creationLabelRow'>
                                                    <span>Preis</span>
                                                </label>
                                                <input
                                                    className='creationInput'
                                                    type='number'
                                                    min='0'
                                                    step='0.01'
                                                    value={line.price}
                                                    onChange={(e) =>
                                                        updateGmbhLine(line.id, {
                                                            price: e.target.value,
                                                        })
                                                    }
                                                    placeholder='z.B. 49.90'
                                                />
                                            </div>

                                            <div className='creationField creationFieldSpan2'>
                                                <label className='creationLabelRow'>
                                                    <span>Information</span>
                                                    <span className='creationOptional'>optional</span>
                                                </label>
                                                <textarea
                                                    className='creationTextarea'
                                                    value={line.information}
                                                    onChange={(e) =>
                                                        updateGmbhLine(line.id, {
                                                            information: e.target.value,
                                                        })
                                                    }
                                                    placeholder='z.B. Zeitraum / Leistungsbeschreibung…'
                                                    rows={3}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className='productTotals'>
                                <div className='productTotalsRow'>
                                    <div className='productTotalsLabel'>
                                        gesetzl. MwSt 19%
                                    </div>
                                    <div className='productTotalsValue'>
                                        {formatEuro(gmbhTotals.vat)}
                                    </div>
                                </div>

                                <div className='productTotalsRow productTotalsRowStrong'>
                                    <div className='productTotalsLabel'>Brutto-Preis</div>
                                    <div className='productTotalsValue'>
                                        {formatEuro(gmbhTotals.gross)}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </section>

                <div className='creationActions creationActionsFull'>
                    <div className='creationSaveState'>
                        <span
                            className={`creationSaveBadge ${
                                saveState === 'saved' ? 'creationSaveBadgeOk' : ''
                            } ${saveState === 'error' ? 'creationSaveBadgeError' : ''}`}
                        >
                            {saveMessage}
                        </span>
                    </div>

                    <button
                        type='button'
                        className='creationButton creationButtonGhost'
                        onClick={doSave}
                    >
                        Speichern
                    </button>

                    <button
                        type='button'
                        className={`creationButton creationButtonPrimary ${
                            !canSend ? 'creationButtonDisabled' : ''
                        }`}
                        onClick={openSendModal}
                        disabled={!canSend}
                        title={!canSend ? 'Bitte zuerst speichern' : 'Senden'}
                    >
                        Dokumente
                    </button>
                </div>

                <footer className='creationFooter'>
                    © Familienkost ·{' '}
                    {new Date().toLocaleDateString('de-DE', {
                        month: 'long',
                        year: 'numeric',
                    })}
                </footer>

                {isSendModalOpen && (
                    <div
                        className='modalOverlay'
                        role='dialog'
                        aria-modal='true'
                        aria-label='Senden bestätigen'
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) closeSendModal()
                        }}
                    >
                        <div className='modalCard'>
                            <div className='modalHeader'>
                                <div
                                    className='modalTitle'>{!sendConfirmOpen ? 'Dokumente' : 'Sendung bestätigen'}</div>
                                <button
                                    type='button'
                                    className='modalClose'
                                    onClick={closeSendModal}
                                    aria-label='Schließen'
                                >
                                    ×
                                </button>
                            </div>

                            <div className='modalBody'>
                                <div className='modalInfoRow'>
                                    <div className='modalInfoLabel'>Rechnungsnummer</div>
                                    <div className='modalInfoValue'>{invoiceNumber}</div>
                                </div>

                                {!sendConfirmOpen ? (
                                    <p className='modalText'>
                                        {issuer === 'gmbh'
                                            ? 'Sie können zuerst das PDF öffnen (Preview), oder direkt senden.'
                                            : 'Sie können Lieferschein herunterladen, die Rechnung öffnen (Preview) oder sie direkt senden.'}
                                    </p>
                                ) : (
                                    <>
                                        <p className='modalText'>
                                            Bitte prüfen Sie die Versanddaten sorgfältig, bevor die Rechnung versendet
                                            wird:
                                        </p>

                                        <div className='modalPreviewBox'>
                                            <div className='modalPreviewTitle'>Empfängeradresse</div>
                                            <div className='modalPreviewText'>
                                                {form.recipient.line1}
                                                <br/>
                                                {form.recipient.line2 && (
                                                    <>
                                                        {form.recipient.line2}
                                                        <br/>
                                                    </>
                                                )}
                                                {form.recipient.street}
                                                <br/>
                                                {form.recipient.zip} {form.recipient.city}
                                                <br/>
                                                {form.recipient.country}
                                            </div>
                                        </div>

                                        <div className='modalPreviewBox'>
                                            <div className='modalPreviewTitle'>E-Mail Versand</div>

                                            <div className='modalPreviewRow'>
                                                <strong>An:</strong> {form.sendEmail}
                                            </div>

                                            <div className='modalPreviewRow'>
                                                <strong>Betreff:</strong> {replacePlaceholders(form.sendSubject)}
                                            </div>

                                            <div className='modalPreviewRow'>
                                                <strong>Text:</strong>
                                                <div className='modalPreviewMailText'>
                                                    {replacePlaceholders(form.sendText)}
                                                </div>
                                            </div>
                                        </div>

                                        {sendError && (
                                            <p className='productsError' style={{marginTop: 12}}>
                                                {sendError}
                                            </p>
                                        )}

                                        {isSending && (
                                            <p className='productsHint' style={{marginTop: 12}}>
                                                Wird gesendet…
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className='modalActions'>
                                {!sendConfirmOpen ? (
                                    <>
                                        {issuer === 'verlag' && (
                                            <button
                                                type='button'
                                                className='creationButton creationButtonGhost'
                                                onClick={doOpenLieferschein}
                                            >
                                                Lieferschein öffnen
                                            </button>
                                        )}

                                        <button
                                            type='button'
                                            className='creationButton creationButtonGhost'
                                            onClick={doOpenPdf}
                                        >
                                            Rechnung öffnen
                                        </button>

                                        <button
                                            type='button'
                                            className='creationButton creationButtonPrimary'
                                            onClick={requestSendConfirmation}
                                        >
                                            Rechnung senden
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type='button'
                                            className='creationButton creationButtonGhost'
                                            onClick={() => setSendConfirmOpen(false)}
                                            disabled={isSending}
                                        >
                                            Abbrechen
                                        </button>

                                        <button
                                            type='button'
                                            className={`creationButton creationButtonPrimary ${isSending ? 'creationButtonDisabled' : ''}`}
                                            onClick={doReallySend}
                                            disabled={isSending}
                                        >
                                            {isSending ? 'Sende…' : 'Direkt senden'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </main>
    )
}

export default CreationPage
